import type { FormField } from '@cli-types/core-engine/schema.js'
import { escapeAttribute, escapeJsx, singularize } from '@cli-utils/text.js'
import { renderFormPipelineListFieldJsxRenderer } from '@next-adapter/templates/renderers/generators/form-pipeline/form-component-shared/generate-list-field-jsx.js'
import { errorMessageLine } from './error-message-line.js'
import { formLabelLine } from './form-label-line.js'
import { generateTextFieldJSX } from './generate-text-field-jsx.js'
import { inputClass } from './input-class.js'
import { labelWithDescriptionLine } from './label-with-description-line.js'
import { selectClass } from './select-class.js'

export function generateListFieldJSX(
  field: FormField,
  name: string,
  label: string,
  hintJSX: string,
  requiredStar: string
): string {
  if (!field.fields || field.fields.length === 0) {
    return generateTextFieldJSX(
      name,
      label,
      field.placeholder || '',
      hintJSX,
      requiredStar,
      'text',
      field.height
    )
  }

  const singularLabel = singularize(label || name)

  const nestedFieldsJSX = field.fields
    .map((nf) => {
      const nfName = nf.name || ''
      const nfLabelRaw = nf.label ? escapeJsx(nf.label) : nfName
      const nfPlaceholder = nf.placeholder || ''

      if (nf.type === 'select' && nf.options && nf.options.length > 0) {
        const selectItems = nf.options
          .map(
            (opt) =>
              `                          <option value="${escapeAttribute(opt.value)}">${escapeJsx(opt.label)}</option>`
          )
          .join('\n')
        return renderFormPipelineListFieldJsxRenderer({
          kind: 'renderListFieldJsxFieldControllerTemplate',
          props: {
            name: name,
            nfName: nfName,
            nfLabelRaw: nfLabelRaw,
            selectClass: selectClass(),
            escapeJsx: escapeJsx(nf.placeholder || 'Select...'),
            selectItems: selectItems,
            errorMessageLine: errorMessageLine('field.name', '                          ')
          }
        })
      }
      if (nf.type === 'number') {
        return renderFormPipelineListFieldJsxRenderer({
          kind: 'renderListFieldJsxFieldControllerFieldNameTemplate',
          props: {
            name: name,
            nfName: nfName,
            nfLabelRaw: nfLabelRaw,
            escapeAttribute: escapeAttribute(nfPlaceholder),
            inputClass: inputClass(),
            errorMessageLine: errorMessageLine('field.name', '                          ')
          }
        })
      }

      return renderFormPipelineListFieldJsxRenderer({
        kind: 'renderListFieldJsxFieldControllerFieldNameFieldTemplate',
        props: {
          name: name,
          nfName: nfName,
          nfLabelRaw: nfLabelRaw,
          escapeAttribute: escapeAttribute(nfPlaceholder),
          inputClass: inputClass(),
          errorMessageLine: errorMessageLine('field.name', '                          ')
        }
      })
    })
    .join('\n')

  const defaultObj = field.fields
    .map((nf) => {
      if (nf.type === 'select' || nf.type === 'radio') return `${nf.name}: undefined`
      if (nf.type === 'checkbox') {
        return nf.options && nf.options.length > 0 ? `${nf.name}: []` : `${nf.name}: false`
      }
      if (nf.type === 'number') return `${nf.name}: undefined`
      return `${nf.name}: ''`
    })
    .join(', ')

  return renderFormPipelineListFieldJsxRenderer({
    kind: 'renderListFieldJsxClickHandlerTemplate',
    props: {
      labelWithDescriptionLine: labelWithDescriptionLine(
        formLabelLine(name, label, requiredStar, 'text-sm font-medium leading-none'),
        hintJSX,
        '          '
      ),
      name: name,
      nestedFieldsJsx: nestedFieldsJSX,
      defaultObj: defaultObj,
      singularLabel: singularLabel
    }
  })
}
